import { useEffect } from 'react';
import { DropDownWrapper } from 'components/common/form/FormSelectDropdown';
import { useAccount } from 'contexts/AccountContext';
import { useProducts } from 'contexts/ProductContext';
import { useSubscriptions } from 'contexts/SubscriptionContext';
import MarketsList from 'constants/markets';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable, useResetRecoilState } from 'recoil';
import { formState, marketsState, countriesState, kitsState, productsState, inputState, refState } from 'state';
import { isEmpty } from 'lodash';
import config from 'config';
import { useField } from 'hooks/useContentfulApi';
import {
  filterMarketsByRef,
  getAllowedMarketIso2s,
  isMarketAllowedForRef,
} from 'helpers/filterMarketsByRef';

const marketsThatAutoSelectCountry = new MarketsList().marketsThatAutoSelectCountry;

export default function MarketDropDown({ isShipping }) {
  const [marketLabel] = useField('banner1Ref.fields.item3');
  const [shippingLabel] = useField('banner1Ref.fields.item5');
  const [form, setForm] = useRecoilState(formState);
  const [inputs, setInputs] = useRecoilState(inputState);
  const { state: marketQueryState, contents: markets } = useRecoilValueLoadable(marketsState);
  const { state: countryQueryState, contents: countries } = useRecoilValueLoadable(countriesState);
  const { businessMarket, countryInfo } = form;
  const ref = useRecoilValue(refState);
  const hasRefRestrictions = Boolean(getAllowedMarketIso2s(ref));

  const { setAccount } = useAccount();
  const { dispatch: productDispatch } = useProducts();
  const { dispatch: subscriptionDispatch } = useSubscriptions();

  useEffect(() => {
    if (!getAllowedMarketIso2s(ref)) return;
    const invalidBusinessMarket =
      businessMarket?.iso2 && !isMarketAllowedForRef(businessMarket.iso2, ref);
    const invalidCountryInfo = countryInfo?.iso2 && !isMarketAllowedForRef(countryInfo.iso2, ref);
    if (!invalidBusinessMarket && !invalidCountryInfo) return;
    setForm((prev) => ({
      ...prev,
      ...(invalidBusinessMarket ? { businessMarket: {}, countryInfo: {} } : {}),
      ...(invalidCountryInfo && !invalidBusinessMarket ? { countryInfo: {} } : {}),
    }));
  }, [ref, businessMarket?.iso2, countryInfo?.iso2, setForm]);

  const toDropdownLabels = (items = []) =>
    items
      .map(({ iso2, label }) => {
        if (!config.FEATURE_FLAG_THAILAND && iso2 === 'TH') return null;
        return label;
      })
      .filter(Boolean);

  const availableMarkets =
    marketQueryState === 'hasValue' ? filterMarketsByRef(markets, ref) : [];
  const availableCountries =
    countryQueryState === 'hasValue'
      ? filterMarketsByRef(
          hasRefRestrictions
            ? countries
            : countries.filter(({ iso2 }) => !marketsThatAutoSelectCountry.includes(iso2)),
          ref,
        )
      : [];

  const marketsOptions = toDropdownLabels(availableMarkets);
  const whitelistedCountries = toDropdownLabels(availableCountries);

  const newProps = {
    id: isShipping ? 'market-options-shipping' : 'market-options',
    options: isShipping ? whitelistedCountries : marketsOptions,
    label: isShipping ? shippingLabel : marketLabel,
    value: isShipping || (countryInfo?.iso2 === 'CN' && businessMarket?.iso2 === 'HK') ? countryInfo?.label : businessMarket?.label,
    onChange,
  };

  // handler functions
  const resetkits = useResetRecoilState(kitsState);
  const resetProducts = useResetRecoilState(productsState);
  const clearCart = () => {
    productDispatch({ type: 'clearCart' });
    subscriptionDispatch({ type: 'clearCart' });
    resetkits();
    resetProducts();
  };

  const resetAccount = () => {
    setAccount({});
  };

  function onChange(label) {
    let newFormData = {};
    let data = !isShipping
      ? markets.find(({ label: marketLabel }) => marketLabel === label)
      : countries.find(({ label: countryLabel }) => countryLabel === label);
    // handle updates based on if market or shipping
    if (isShipping) {
      newFormData = { countryInfo: data };
    } else if (!isShipping && data?.iso2 === 'CN') {
      const hkMarket = markets.find(({ iso2 }) => iso2 === 'HK');
      newFormData = { businessMarket: hkMarket, countryInfo: data };
    } else {
      newFormData = marketsThatAutoSelectCountry.includes(data?.iso2)
        ? // for these markets we set the country to the same as the market
          { businessMarket: data, countryInfo: data }
        : // for these countries we set the market country then allow them to choose their shipping country
          { businessMarket: data, countryInfo: {} };
    }

    const addressFields = [
      'ACCOUNT_ADDRESS1',
      'ACCOUNT_ADDRESS2',
      'ACCOUNT_ADDRESS3',
      'ACCOUNT_ADDRESS4',
      'ACCOUNT_ADDRESS5',
      'ACCOUNT_CITY',
      'ACCOUNT_STATE',
      'ACCOUNT_ZIP',
      'ACCOUNT_COUNTY',
      'SHIPPING_ADDRESS1',
      'SHIPPING_ADDRESS2',
      'SHIPPING_ADDRESS3',
      'SHIPPING_ADDRESS4',
      'SHIPPING_ADDRESS5',
      'SHIPPING_CITY',
      'SHIPPING_STATE',
      'SHIPPING_ZIP',
      'SHIPPING_COUNTY',
    ];
    const clearedFields = addressFields.reduce((acc, key) => {
      acc[key] = '';
      return acc;
    }, {});

    setInputs((prev) => ({ ...prev, ...clearedFields }));

    // onChange actions
    clearCart();
    resetAccount();
    setForm({ ...form, ...newFormData });
  }

  // useEffect(() => {
  //   if (!isShipping && marketParam?.name && markets?.length && !businessMarket?.iso2) {
  //     onChange(marketParam?.name);
  //   }
  // }, [marketParam?.name, markets?.length, businessMarket?.iso2, isShipping]);

  if (
    isShipping &&
    (marketsThatAutoSelectCountry.includes(businessMarket?.iso2) ||
      isEmpty(businessMarket) ||
      countryInfo?.iso2 === 'CN')
  )
    return null;

  return <DropDownWrapper {...newProps} />;
}
